import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { eventsAPI, unwrapList } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import './Pos.css';

const emptyForm = {
  title: '',
  description: '',
  event_date: '',
  location: '',
  address: '',
  max_participants: 0,
  registration_deadline: '',
  require_registration: true,
  allow_walk_in: false,
  require_verified: false,
  gift_batch: '',
  status: 'upcoming',
  is_published: true,
};

function toLocalInput(value) {
  if (!value) return '';
  return String(value).replace(' ', 'T').slice(0, 16);
}

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [batches, setBatches] = useState([]);
  const [newBatchName, setNewBatchName] = useState('');
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    eventsAPI.giftBatches()
      .then((response) => setBatches(unwrapList(response.data)))
      .catch(() => setBatches([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    eventsAPI.adminGet(id)
      .then((response) => {
        const event = response.data;
        setForm({
          ...emptyForm,
          ...event,
          event_date: toLocalInput(event.event_date),
          registration_deadline: toLocalInput(event.registration_deadline),
          gift_batch: event.gift_batch || '',
          address: event.address || '',
        });
      })
      .catch(() => error('找不到活動'))
      .finally(() => setLoading(false));
  }, [id, error]);

  const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      let giftBatchId = form.gift_batch || null;
      if (newBatchName.trim()) {
        const created = await eventsAPI.createGiftBatch({
          name: newBatchName.trim(),
          is_active: true,
        });
        giftBatchId = created.data.id;
      }
      const payload = {
        ...form,
        max_participants: Number(form.max_participants) || 0,
        gift_batch: giftBatchId,
        registration_deadline: form.registration_deadline || null,
        event_date: form.event_date,
      };
      delete payload.registered_count;
      delete payload.attendance_count;
      delete payload.is_registration_open;
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.image;
      if (id) {
        await eventsAPI.adminUpdate(id, payload);
      } else {
        await eventsAPI.adminCreate(payload);
      }
      success('活動已儲存');
      navigate('/admin/events');
    } catch (err) {
      error('活動儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingPage message="載入活動..." />;

  return (
    <div className="pos-card">
      <p><Link to="/admin/events">返回活動</Link></p>
      <h1>{id ? '編輯活動' : '新增活動'}</h1>
      <form className="pos-form-grid" onSubmit={onSubmit}>
        <label>標題
          <input value={form.title} onChange={(e) => setField('title', e.target.value)} required />
        </label>
        <label>描述
          <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} required />
        </label>
        <label>日期時間
          <input type="datetime-local" value={form.event_date} onChange={(e) => setField('event_date', e.target.value)} required />
        </label>
        <label>地點
          <input value={form.location} onChange={(e) => setField('location', e.target.value)} required />
        </label>
        <label>地址
          <input value={form.address} onChange={(e) => setField('address', e.target.value)} />
        </label>
        <label>人數上限（0 表示不限）
          <input type="number" min="0" value={form.max_participants} onChange={(e) => setField('max_participants', e.target.value)} />
        </label>
        <label>報名截止
          <input type="datetime-local" value={form.registration_deadline} onChange={(e) => setField('registration_deadline', e.target.value)} />
        </label>
        <label>狀態
          <select value={form.status} onChange={(e) => setField('status', e.target.value)}>
            <option value="upcoming">即將舉行</option>
            <option value="ongoing">進行中</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
          </select>
        </label>
        <label>禮品批次
          <select value={form.gift_batch || ''} onChange={(e) => setField('gift_batch', e.target.value)}>
            <option value="">沒有</option>
            {batches.map((batch) => (
              <option key={batch.id} value={batch.id}>{batch.name}</option>
            ))}
          </select>
        </label>
        <label>新禮品批次名稱（選填）
          <input value={newBatchName} onChange={(e) => setNewBatchName(e.target.value)} />
        </label>
        <label className="pos-check">
          <input type="checkbox" checked={form.require_registration} onChange={(e) => setField('require_registration', e.target.checked)} />
          需要報名
        </label>
        <label className="pos-check">
          <input type="checkbox" checked={form.allow_walk_in} onChange={(e) => setField('allow_walk_in', e.target.checked)} />
          允許現場報名
        </label>
        <label className="pos-check">
          <input type="checkbox" checked={form.require_verified} onChange={(e) => setField('require_verified', e.target.checked)} />
          只限已驗證會員
        </label>
        <div className="pos-actions">
          <button className="pos-btn" type="submit" disabled={saving}>{saving ? '儲存中...' : '儲存'}</button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
